import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { BookOpen, ArrowLeft, ChevronRight } from 'lucide-react';
import { RiyalIcon } from '@/lib/currency';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { ReportFilterBar } from '@/components/reports/ReportFilterBar';
import { FilterEmptyNudge } from '@/components/common/FilterEmptyNudge';
import { PageSizeSelector } from '@/components/common/PageSizeSelector';
import { BranchCoverageNotice, forbiddenDetail } from '@/components/accounting/BranchCoverageNotice';
import { localToday, toLocalYmd } from '@/lib/dateRange';

export default function GeneralLedgerPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [accounts, setAccounts] = useState([]);
  const [blocked, setBlocked] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [entries, setEntries] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 0 });
  const [glPageSize, setGlPageSize] = useState(50);
  const [startDate, setStartDate] = useState(toLocalYmd(new Date(new Date().getFullYear(), new Date().getMonth(), 1)));
  const [endDate, setEndDate] = useState(localToday());
  const [loading, setLoading] = useState(true);
  // Universal filter: default = all permitted branches (server enforces permissions).
  const [branches, setBranches] = useState([]);
  const [branchSel, setBranchSel] = useState([]);
  const [withOpening, setWithOpening] = useState(true);
  const [detailMeta, setDetailMeta] = useState(null);
  const [allEntries, setAllEntries] = useState(null);
  const branchQS = branchSel.length ? `&branch_ids=${branchSel.join(',')}` : '';

  const fetchSummary = async (o = {}) => {
    setLoading(true);
    try {
      const _sd = o.startDate !== undefined ? o.startDate : startDate;
      const _ed = o.endDate !== undefined ? o.endDate : endDate;
      const _bs = o.branchSel !== undefined ? o.branchSel : branchSel;
      const _bqs = _bs.length ? `&branch_ids=${_bs.join(',')}` : '';
      const res = await api.get(`/accounting/general-ledger?start_date=${_sd}&end_date=${_ed}${_bqs}`);
      setAccounts(res.data.accounts || []); setCoverage(res.data.branch_coverage || null); setBlocked(null);
    } catch (e) { const d = forbiddenDetail(e); if (d) { setBlocked(d); setAccounts([]); } else toast.error('Failed to load ledger'); }
    finally { setLoading(false); }
  };

  const fetchAccountDetail = async (code, page = 1) => {
    setLoading(true);
    try {
      const base = `/accounting/general-ledger?account_code=${encodeURIComponent(code)}&start_date=${startDate}&end_date=${endDate}&include_opening=${withOpening}${branchQS}`;
      const res = await api.get(`${base}&page=${page}&limit=${Math.min(glPageSize, 200)}`);
      setSelectedAccount(res.data.account);
      setEntries(res.data.entries || []);
      setPagination({ total: res.data.total, page: res.data.page, pages: res.data.pages });
      setDetailMeta({ opening: res.data.opening, movement: res.data.movement, closing: res.data.closing });
      setCoverage(res.data.branch_coverage || null);
      setAllEntries(null); // preload every row so exports download inside the click and match the screen
      api.get(`${base}&all_rows=true`).then((r) => setAllEntries(r.data.entries || [])).catch(() => {});
    } catch (e) { const d = forbiddenDetail(e); if (d) setBlocked(d); else toast.error('Failed to load account detail'); }
    finally { setLoading(false); }
  };

  // Lines with no branch on the line or its journal are selectable as "Unassigned" — never guessed or hidden.
  useEffect(() => { api.get('/branches').then(r => setBranches([...(r.data || []), { id: '__unassigned__', name: 'Unassigned (no branch)' }])).catch(() => {}); }, []);
  // iter966 — refetch account detail when GL page-size changes.
  useEffect(() => {
    if (selectedAccount) fetchAccountDetail(selectedAccount.code, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [glPageSize]);
  // Refetch whenever the shared filters change (branches, dates, opening).
  useEffect(() => {
    if (selectedAccount) fetchAccountDetail(selectedAccount.code);
    else fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchSel, startDate, endDate, withOpening]);

  const branchLabel = branchSel.length ? branches.filter((b) => branchSel.includes(b.id)).map((b) => b.name).join(', ') : 'All permitted branches';
  const n2 = (v) => (v == null ? '' : Number(v).toFixed(2));
  const exportSpec = () => (selectedAccount ? {
    title: `General Ledger — ${selectedAccount.code} ${selectedAccount.name}`, subtitle: `${startDate} to ${endDate} · ${branchLabel}`,
    filename: `gl_${selectedAccount.code}_${startDate}_${endDate}`,
    cols: [{ key: 'date', label: 'Date', format: (v) => String(v || '').slice(0, 10) }, { key: 'entry_number', label: 'Entry #' }, { key: 'reference', label: 'Reference' },
      { key: 'description', label: 'Description' }, { key: 'debit', label: 'Debit', numeric: true, format: n2 }, { key: 'credit', label: 'Credit', numeric: true, format: n2 },
      { key: 'running_balance', label: 'Balance', numeric: true, format: n2 }],
    rows: [...(detailMeta?.opening ? [{ description: `Opening balance as of ${detailMeta.opening.as_of}`, running_balance: detailMeta.opening.balance }] : []),
      ...(allEntries || []),
      { description: 'Period movement', debit: detailMeta?.movement?.debit, credit: detailMeta?.movement?.credit },
      { description: withOpening ? 'Closing balance' : 'Net movement (opening OFF)', running_balance: detailMeta?.closing }],
  } : {
    title: 'General Ledger — account summary', subtitle: `${startDate} to ${endDate} · ${branchLabel}`, filename: `gl_summary_${startDate}_${endDate}`,
    cols: [{ key: 'code', label: 'Code' }, { key: 'name', label: 'Account' }, { key: 'type', label: 'Type' }, { key: 'total_debit', label: 'Debit', numeric: true, format: n2 },
      { key: 'total_credit', label: 'Credit', numeric: true, format: n2 }, { key: 'net_balance', label: 'Net', numeric: true, format: n2 }, { key: 'transaction_count', label: 'Txns', numeric: true }],
    rows: accounts,
  });

  // Deep-link support. Accept URL params: account | account_code | start_date | end_date
  useEffect(() => {
    const acc = searchParams.get('account') || searchParams.get('account_code');
    const sd = searchParams.get('start_date');
    const ed = searchParams.get('end_date');
    if (sd) setStartDate(sd);
    if (ed) setEndDate(ed);
    if (acc) {
      // Fetch details directly. Backend matches code OR name.
      fetchAccountDetail(acc);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-5xl mx-auto" data-testid="general-ledger-page">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            {selectedAccount && (
              <Button variant="ghost" size="sm" onClick={() => { setSelectedAccount(null); fetchSummary(); }} data-testid="gl-back-btn">
                <ArrowLeft size={16} />
              </Button>
            )}
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center shadow-lg shadow-teal-200/50">
                <BookOpen size={24} className="text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground">
                  {t('general_ledger_title') || 'General Ledger'}
                </h1>
                {selectedAccount && <p className="text-sm text-muted-foreground mt-0.5">{selectedAccount.code} — {selectedAccount.name}</p>}
              </div>
            </div>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <ReportFilterBar testId="gl-filter" branches={branches} branchIds={branchSel} onBranchIds={setBranchSel}
              range={{ start: startDate, end: endDate }} onRange={(r) => { setStartDate(r.start); setEndDate(r.end); }}
              opening={withOpening} onOpening={selectedAccount ? setWithOpening : undefined}
              exportSpec={exportSpec} exportDisabled={selectedAccount ? !allEntries : !accounts.length} />
          </div>
        </div>

        <BranchCoverageNotice unavailable={blocked} coverage={coverage} testid="gl-coverage" />
        {loading ? (
          <div className="flex items-center justify-center min-h-[40vh]">
            <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : !selectedAccount ? (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Account Summary</CardTitle></CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm" data-testid="gl-summary-table">
                <thead>
                  <tr className="border-b bg-muted/50 dark:bg-foreground text-muted-foreground">
                    <th className="text-left p-3 font-medium">Code</th>
                    <th className="text-left p-3 font-medium">Account</th>
                    <th className="text-center p-3 font-medium">Type</th>
                    <th className="text-right p-3 font-medium">Debit</th>
                    <th className="text-right p-3 font-medium">Credit</th>
                    <th className="text-right p-3 font-medium">Net</th>
                    <th className="text-center p-3 font-medium">Txns</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.length === 0 && <tr><td colSpan={8} className="p-0"><FilterEmptyNudge show onReset={() => { setSelectedAccount(null); const td = localToday(); setStartDate(td); setEndDate(td); setBranchSel([]); fetchSummary({ startDate: td, endDate: td, branchSel: [] }); }} label="No journal entries for the current filter" /></td></tr>}
                  {accounts.map((a, i) => (
                    <tr key={i} className="border-b hover:bg-muted/50 cursor-pointer" onClick={() => fetchAccountDetail(a.code || a.name)} data-testid={`gl-summary-row-${i}`}>
                      <td className="p-3 font-mono text-muted-foreground">{a.code}</td>
                      <td className="p-3 font-medium">{a.name}</td>
                      <td className="p-3 text-center"><Badge variant="outline" className="capitalize text-xs">{a.type}</Badge></td>
                      <td className="p-3 text-right font-mono text-info">{a.total_debit.toLocaleString()}</td>
                      <td className="p-3 text-right font-mono text-error">{a.total_credit.toLocaleString()}</td>
                      <td className={`p-3 text-right font-mono font-bold ${a.net_balance >= 0 ? 'text-success' : 'text-error'}`}>{a.net_balance.toLocaleString()}</td>
                      <td className="p-3 text-center text-muted-foreground">{a.transaction_count}</td>
                      <td className="p-3"><ChevronRight size={16} className="text-muted-foreground" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm" data-testid="gl-detail-table">
                <thead>
                  <tr className="border-b bg-muted/50 dark:bg-foreground text-muted-foreground">
                    <th className="text-left p-3 font-medium">Date</th>
                    <th className="text-left p-3 font-medium">Entry #</th>
                    <th className="text-left p-3 font-medium">Description</th>
                    <th className="text-right p-3 font-medium">Debit</th>
                    <th className="text-right p-3 font-medium">Credit</th>
                    <th className="text-right p-3 font-medium">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {detailMeta?.opening && pagination.page === 1 && (
                    <tr className="border-b bg-muted/30" data-testid="gl-opening-row">
                      <td className="p-3 text-muted-foreground">{detailMeta.opening.as_of}</td><td className="p-3" colSpan={4}>Opening balance (all postings before {startDate})</td>
                      <td className="p-3 text-right font-mono font-bold">{detailMeta.opening.balance.toLocaleString()}</td>
                    </tr>
                  )}
                  {entries.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No entries for this account</td></tr>}
                  {entries.map((e, i) => (
                    <tr
                      key={i}
                      className="border-b hover:bg-primary/5 cursor-pointer transition-colors group"
                      onClick={() => {
                        const q = new URLSearchParams();
                        if (e.entry_number) q.set('entry_number', e.entry_number);
                        navigate(`/journal-entries?${q.toString()}`);
                      }}
                      title="Click to view this journal entry"
                      data-testid={`gl-entry-row-${i}`}
                    >
                      <td className="p-3 text-muted-foreground">{e.date?.split('T')[0]}</td>
                      <td className="p-3 font-mono text-xs group-hover:text-primary font-medium">{e.entry_number || '—'}</td>
                      <td className="p-3">{e.description}</td>
                      <td className="p-3 text-right font-mono text-info">{e.debit > 0 ? e.debit.toLocaleString() : '—'}</td>
                      <td className="p-3 text-right font-mono text-error">{e.credit > 0 ? e.credit.toLocaleString() : '—'}</td>
                      <td className={`p-3 text-right font-mono font-bold ${e.running_balance >= 0 ? 'text-success' : 'text-error'}`}>{e.running_balance?.toLocaleString()}</td>
                    </tr>
                  ))}
                  {detailMeta && pagination.page >= pagination.pages && (
                    <tr className="bg-muted/30 font-semibold" data-testid="gl-closing-row">
                      <td className="p-3" colSpan={3}>{withOpening ? 'Closing balance' : 'Net movement (opening OFF)'}</td>
                      <td className="p-3 text-right font-mono text-info">{detailMeta.movement?.debit?.toLocaleString()}</td>
                      <td className="p-3 text-right font-mono text-error">{detailMeta.movement?.credit?.toLocaleString()}</td>
                      <td className="p-3 text-right font-mono" data-testid="gl-closing-balance">{detailMeta.closing?.toLocaleString()}</td>
                    </tr>
                  )}
                </tbody>
              </table>
              {selectedAccount && (
                <div className="flex justify-between items-center gap-2 p-4 flex-wrap">
                  <PageSizeSelector value={glPageSize} onChange={(n) => { setGlPageSize(n); }} testid="gl-page-size" />
                  <div className="flex gap-2 flex-wrap">
                    {pagination.pages > 1 && Array.from({ length: pagination.pages }, (_, i) => (
                      <Button key={i} size="sm" variant={i + 1 === pagination.page ? 'default' : 'outline'} onClick={() => fetchAccountDetail(selectedAccount.code, i + 1)}>{i + 1}</Button>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
